import { fetchTasksGetById } from '~/backend/connector-next-api/connector-next-components';
import {
    createOutageTolerance,
    POLL_OUTAGE_TOLERANCE_MS,
} from '~/backend/network-retry';
import { TaskView } from '~/backend/connector-next-api/connector-next-schemas';
import { downloadTaskResults } from '~/backend/connector-next-api/taskResults';
import { FINISHED_JOB_STATUSES, Job, TaskStatus } from '~/state/atoms/jobAtoms';
import { handleError } from '~/utils/errors';

const DEFAULT_POLLING_INTERVAL = 2000;

/**
 * Connector task states mapped onto the statuses the job queue displays.
 * The map is complete for the connector's state set. An unmapped state is a
 * contract mismatch, not progress, so it is reported instead of displayed.
 */
const JOB_STATUS_BY_TASK_STATE: Record<string, TaskStatus> = {
    AwaitingData: 'Received',
    Submitted: 'Uploading',
    Running: 'Processing',
    Completed: 'Done',
    Failed: 'Error',
    Cancelled: 'Canceled',
};

/**
 * Maps a connector task state to the job status the UI shows.
 *
 * @param state - `state` from the connector task view.
 * @throws If the state is missing or not one the UI knows.
 */
export function jobStatusFromTaskState(
    state: string | null | undefined
): TaskStatus {
    const status = state ? JOB_STATUS_BY_TASK_STATE[state] : undefined;
    if (!status) {
        throw new Error(`Unknown connector task state: ${state ?? 'missing'}`);
    }
    return status;
}

/**
 * The task id the connector assigned. The generated schema marks it optional,
 * but a response without one cannot be polled or cancelled.
 *
 * @param task - Task view returned by a create call.
 * @throws If the response carries no id.
 */
export function requireTaskId(task: TaskView): string {
    if (!task.id) {
        throw new Error('Connector task response has no id.');
    }
    return task.id;
}

/** A task was refused before submission, and the user was told why. */
export class TaskRefusedError extends Error {
    constructor(message: string, options?: { cause?: unknown }) {
        super(message, options);
        this.name = 'TaskRefusedError';
    }
}

/**
 * Reports a task that did not start. A {@link TaskRefusedError} already told
 * the user why, so it adds nothing. Any other error is logged, sent to error
 * tracking, and shown to the user with `message`.
 */
export function handleTaskStartError(error: unknown, message: string): void {
    if (error instanceof TaskRefusedError) {
        return;
    }
    handleError(error, message);
}

/**
 * Periodically polls the connector for a task until it reaches a finished state.
 * It reports status changes via a callback as it polls.
 *
 * @param job - The job being processed, which contains the task ID.
 * @param onStatusChange - A callback function invoked with the job ID and new status when a change is detected.
 * @param intervalMs - The time in milliseconds between polling attempts.
 * @returns A promise that resolves with the final task view when the job is complete.
 * @throws Rejects the promise if polling fails with a non-transient error,
 *     or keeps failing transiently for {@link POLL_OUTAGE_TOLERANCE_MS}.
 */
export async function pollTaskStatus(
    job: Job,
    onStatusChange: (
        jobId: string,
        status: Job['status'],
        errorMessage?: string
    ) => void,
    intervalMs = DEFAULT_POLLING_INTERVAL
): Promise<TaskView> {
    const jobId = job.id;
    // The connector keeps running the task whatever happens to this
    // tab's network, so a blip must not fail the job and strand its
    // result: transient failures keep the loop polling for a bounded
    // outage. The loop's interval is the retry spacing, so each poll is
    // a single attempt.
    const outage = createOutageTolerance(POLL_OUTAGE_TOLERANCE_MS);
    return new Promise((resolve, reject) => {
        const fail = (err: unknown): void => {
            const reason = err instanceof Error ? err.message : String(err);
            reject(new Error(`Job ${jobId}: Polling failed. ${reason}`));
        };
        const poll = async () => {
            // Only the request is inside this try: the outage tolerance
            // reads a failure's type, and a defect in the handling below
            // must fail the job at once, not be polled through.
            let task: TaskView;
            try {
                task = await fetchTasksGetById({
                    pathParams: { id: jobId },
                    retryPolicy: 'none',
                });
            } catch (err) {
                if (outage.tolerate(err)) {
                    setTimeout(poll, intervalMs);
                    return;
                }
                fail(err);
                return;
            }
            outage.reset();
            try {
                const status = jobStatusFromTaskState(task.state);

                if (FINISHED_JOB_STATUSES.includes(status)) {
                    resolve(task);
                } else {
                    if (job.status !== status) {
                        onStatusChange(jobId, status);
                    }
                    setTimeout(poll, intervalMs);
                }
            } catch (err) {
                fail(err);
            }
        };
        void poll();
    });
}

export type ConnectorDownloadedResults = {
    files: File[];
    downloadErrors: string[];
};

/**
 * Downloads every result file a finished connector job produced. A failed task
 * reports its reason on the task view itself, so there are no error files to
 * fetch here.
 *
 * @param pollData - The final task view from the polling process.
 * @returns The downloaded files, plus a message for each file that failed to download.
 */
export async function downloadConnectorResults(
    pollData: TaskView
): Promise<ConnectorDownloadedResults> {
    const resultPaths = pollData.results ?? [];
    if (resultPaths.length === 0) {
        return { files: [], downloadErrors: [] };
    }

    const outcome = await downloadTaskResults(
        requireTaskId(pollData),
        resultPaths
    );

    return { files: outcome.files, downloadErrors: outcome.errors };
}
